// @ts-check
import {
  Flex,
  Box,
  Button,
  CloseIcon,
  Link,
  SuccessIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '~/design-system';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
const EMPTY = '(empty)';

/**
 * Renders the table to map the fields
 * @param {Object} props
 * @param {Object} props.bottlebooksFieldsMap - the mapped fields
 * @param {{name: string, sampleRows: string[]}[]} props.sourceFields - the options to be rendered on the select boxes
 * @param {Object[]} props.fieldsToImport - the bottlebooks fields
 * @param {function} props.onChange - callback when one field is mapped
 */
export default function FieldsTable({
  bottlebooksFieldsMap,
  sourceFields,
  fieldsToImport,
  onChange,
  ...rest
}) {
  return (
    <Table
      sx={{
        border: '1px solid',
        borderColor: 'brand.lightGray',
        width: '100%',
      }}
      {...rest}
    >
      <TableHead>
        <MapFieldHeaderRow />
      </TableHead>
      <TableBody>
        {fieldsToImport?.map((column, index) => (
          <MapFieldRow
            key={column.key}
            column={column}
            importFieldName={
              bottlebooksFieldsMap?.[column.key]?.importFieldName
            }
            sourceFields={sourceFields}
            isRequired={column.required}
            onChange={onChange}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function MapFieldHeaderRow() {
  return (
    <TableRow>
      <TableHeader
        sx={{
          backgroundColor: 'brand.lightGray',
          textAlign: 'center',
          width: 150,
        }}
      >
        <Text variant="smallest">
          <Trans>Matched</Trans>
        </Text>
      </TableHeader>
      <TableHeader sx={{ width: 250, backgroundColor: 'brand.lightGray' }}>
        <Text variant="smallest">
          <Trans>Bottlebooks field name</Trans>
        </Text>
      </TableHeader>
      <TableHeader sx={{ width: 350, backgroundColor: 'brand.lightGray' }}>
        <Text variant="smallest">
          <Trans>Import column name</Trans>
        </Text>
      </TableHeader>
      <TableHeader sx={{ width: 300, backgroundColor: 'brand.lightGray' }}>
        <Text variant="smallest">
          <Trans>Data preview</Trans>
        </Text>
      </TableHeader>
    </TableRow>
  );
}

/**
 * @param {object} props
 * @param {{label: string, description: string, key: string, required: boolean}} props.column
 * @param {string} props.importFieldName
 * @param {{name: string, sampleRows: string[]}[]} props.sourceFields
 * @param {boolean} props.isRequired
 * @param {function} props.onChange
 * @returns
 */
function MapFieldRow({
  column,
  importFieldName,
  sourceFields,
  isRequired,
  onChange,
}) {
  const { i18n } = useLingui();
  return (
    <TableRow key={column.key}>
      <TableCell sx={{ height: '90px', textAlign: 'center' }}>
        {importFieldName && (
          <SuccessIcon sx={{ color: 'brand.successGreen' }} />
        )}
      </TableCell>
      <TableCell
        sx={{ height: '90px', textAlign: 'left', position: 'relative' }}
      >
        <Text variant="default">{i18n._(column.label)}</Text>
        <Text variant="small">{column.description}</Text>
        {!isRequired && (
          <Text
            variant="smallest"
            sx={{
              color: 'brand.smoke',
              position: 'absolute',
              bottom: '2px',
            }}
          >
            Optional
          </Text>
        )}
      </TableCell>
      <TableCell sx={{ height: '90px', textAlign: 'left' }}>
        <MatchOptions
          sourceFieldNames={sourceFields?.map(({ name }) => name)}
          importFieldName={importFieldName}
          isRequired={isRequired}
          onChange={(value) => {
            onChange(value, column);
          }}
        />
      </TableCell>
      <TableCell sx={{ height: '90px', textAlign: 'left' }}>
        <SampleRows
          selectedOption={importFieldName}
          sampleRows={
            sourceFields?.find(({ name }) => name === importFieldName)
              ?.sampleRows
          }
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * Renders 3 samples for a given source field
 * @param {object} props
 * @param {string} props.selectedOption
 * @param {string[]} [props.sampleRows]
 * @returns
 */
function SampleRows({ selectedOption, sampleRows }) {
  if (!sampleRows?.length) return null;

  return (
    <Box>
      {sampleRows.map((value, index) => (
        <Text
          key={index} // We need to cheat here because values aren't unique.
          sx={{ maxWidth: '230px' }}
          nowrap={1}
          variant="smallest"
        >
          {value}
        </Text>
      ))}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string[]} props.sourceFieldNames
 * @param {boolean} props.isRequired
 * @param {string} props.importFieldName
 * @param {function} props.onChange
 * @returns
 */
function MatchOptions({
  sourceFieldNames,
  isRequired,
  importFieldName,
  onChange,
}) {
  if (isRequired) {
    return (
      <Options
        sourceFieldNames={sourceFieldNames}
        importFieldName={importFieldName}
        onChange={onChange}
      />
    );
  }

  return (
    <NotRequiredOption
      sourceFieldNames={sourceFieldNames}
      importFieldName={importFieldName}
      onChange={onChange}
    />
  );
}
/**
 * Renders optional import column name
 * @param {object} props
 * @param {string[]} props.sourceFieldNames
 * @param {string} props.importFieldName
 * @param {function} props.onChange
 * @returns
 */
function NotRequiredOption({ sourceFieldNames, importFieldName, onChange }) {
  const [optionsVisible, setOptionsVisible] = React.useState(importFieldName);

  if (optionsVisible || importFieldName) {
    return (
      <Flex>
        <Options
          sourceFieldNames={sourceFieldNames}
          importFieldName={importFieldName}
          onChange={onChange}
        />

        <Button
          variant="text"
          sx={{ marginLeft: 2, lineHeight: '17px' }}
          onClick={() => {
            setOptionsVisible(false);
            onChange(EMPTY);
          }}
        >
          <CloseIcon size="xxsmall" />
        </Button>
      </Flex>
    );
  }
  return <Link onClick={() => setOptionsVisible(true)}>Add...</Link>;
}

function Options({ sourceFieldNames, importFieldName, onChange }) {
  return (
    <Autocomplete
      sx={{ width: '100%' }}
      options={sourceFieldNames.filter(Boolean)}
      disablePortal
      getOptionLabel={(sourceFieldName) => sourceFieldName}
      getOptionSelected={(sourceFieldName, v) => {
        return sourceFieldName;
      }}
      value={importFieldName}
      onChange={(e, value) => {
        onChange(value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name="selectBBFieldName"
          label=""
          variant="outlined"
          fullWidth
        />
      )}
    />
  );
}
